/**
 * [DUBAI 2026-10-19, user] GITEX GLOBAL Dubai contest — the TV board, "Flight to Dubai" (the look the user chose).
 *
 * Reached only by the board's own sign-in (admin + its password; no button anywhere — user: "this dashboard will fully
 * work on the login using the credentials"). Built like the PM Singapore board, whose TV rules it keeps:
 *   • FULL BLEED: portalled to <body>, a fixed 1600 x 900 canvas scaled to the screen with CSS zoom (text stays sharp on
 *     a 4K panel), letterboxed black on a screen that is not 16:9.
 *   • THE CIRCUIT: Standings 60 s → one other page drawn at random (never the same twice running; Scorecard, The Chase,
 *     Second Voice 60 s — New Deal is NOT in it, [DUBAI-DEAL-ONLY]) → Standings … ([DUBAI-REPLAY]: Standings and Scorecard
 *     replay their opening animation every 30 s; [DUBAI-CHASE-20]: The Chase brings in the next group every 20 s, so one
 *     visit shows every name and the next visit starts again from the top). A new deal interrupts for 30 s and the page it
 *     interrupted resumes with the time it had left. A click / key holds a page for 60 s, then the circuit carries on.
 *   • NEW DEAL: a deal the server counts for the first time takes the screen for 30 s — its own GIF (the 18 notifier GIFs,
 *     a shuffled deck) and its own Dubai sound (the five sounds, a shuffled deck) — then the board goes back to the page it
 *     interrupted. Several at once are shown one after another. [DUBAI-SOUND-PAGE] EVERY arrival on the New Deal page plays
 *     the next sound — a new deal, or a person picking New Deal (bar button / key 5); it starts when the page is drawn,
 *     fades when that page leaves, and never plays while the board is out of sight.
 *   • "WITHIN SECONDS" (user: a mapped recurring new sale must reflect within seconds): the board asks every 4 s with the
 *     stamp it holds and gets a few bytes back while nothing moved; the server recomputes ~1 s after the data-version
 *     watcher sees a deal / payment / refund / dispute change (services/dubaiContest.ts).
 *   • TELEVISION: it reloads itself onto a new build (3-minute settle, probe, loop guard — PMSingaporePage's rules) behind
 *     its own full-screen update screen ([DUBAI-UPDATE]: the gate closes, "NEW VERSION · NOW BOARDING", 5 s — PM Singapore's
 *     chequered flag), and holds the app-wide updater off so nothing reloads it plainly. A dropped connection just keeps the
 *     last figures ([DUBAI-NO-LINE]).
 * The pages themselves are components/dubaiContest/flightPages.ts; every number comes from the server.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { holdReload, releaseReload } from '../services/reloadGuard'; // [DUBAI-UPDATE] the board owns its own update
import { DubaiBoardStyles } from '../components/dubaiContest/boardStyles';
import { DubaiExtraStyles } from '../components/dubaiContest/extraStyles'; // [DUBAI-GATES] app-only board styles
import { buildModel, DubaiPayload, Model, DubaiDeal } from '../components/dubaiContest/model';
import { FLIGHT_PAGES, renderDealPage, PageId, Loop, PLANE_SVG, REDUCED, CHASE_VISIT_MS, chaseVisit } from '../components/dubaiContest/flightPages';
import { gifFor, warmNextGif } from '../components/dubaiContest/deck';
import { armAudio, playCue, stopCue, wasBlocked, isMuted, setMuted, primeAudio, prepareSounds, unlockAudio } from '../components/dubaiContest/sound';

/* [DUBAI-FILL 2026-10-19, user] "it should cover the complete page": the HEIGHT is fixed and the WIDTH follows the screen's
   shape (PMSingaporePage's [PM-SG FILL] rule). A 16:9 TV gets exactly the approved 1600 x 900 composition; a wider browser
   window gets a wider canvas that the pages fill edge to edge (flightPages.ts reads data-cw). Only a screen narrower than 16:9
   or wider than ~2.4:1 letterboxes. */
const W = 1600;
const MAX_W = 2200;
const H = 900;
const POLL_MS = 4000;
/* [DUBAI-REPLAY 2026-10-19, user] How long each page holds the screen: Standings, Scorecard, The Chase and Second Voice one
   minute each (Standings and Scorecard replay their opening animation every 30 s; The Chase's minute is its three 20-second
   groups, [DUBAI-CHASE-20] — CHASE_VISIT_MS); New Deal keeps 90 s. */
const DWELL: Record<PageId, number> = { standings: 60000, scorecard: 60000, chase: CHASE_VISIT_MS, voice: 60000, deal: 90000 };
const ANNOUNCE_MS = 30000;
const HOLD_MS = 60000;
/* [DUBAI-DEAL-ONLY 2026-10-19, user] "the new deal page will not be included in the auto page switch thing, it will only come
   when new deal for the recurring new sale will be mapped": the circuit draws from these three only. */
const GUESTS: PageId[] = ['scorecard', 'chase', 'voice'];
const PAGES: { id: PageId; label: string }[] = [
  { id: 'standings', label: 'Standings' }, { id: 'scorecard', label: 'Scorecard' }, { id: 'chase', label: 'The Chase' },
  { id: 'voice', label: 'Second Voice' }, { id: 'deal', label: 'New Deal' },
];
/** The newest announcement this screen has already taken — kept across the board's own reloads for a quarter of an hour,
 *  so a deploy-reload neither replays a celebration nor swallows one that landed during the reload. */
const SEEN_KEY = 'dubai-seen-at';
const SEEN_WINDOW_MS = 15 * 60 * 1000;
const VERSION_POLL_MS = 30000;
const SETTLE_MS = 3 * 60000;
const RELOAD_RETRY_MS = 300000;
const RELOAD_KEY = 'dubai-reload-to';
const AUTH_RELOAD_KEY = 'dubai-auth-reload';
/** [DUBAI-UPDATE] How long the update screen is up before the reload fires underneath it — PM Singapore's FLAG_MS (5 s). */
const FLAG_MS = 5000;
/** [DUBAI-UPDATE] The reloadGuard hold that keeps the app-wide updater off this board (see the reload effect). */
const BOARD_HOLD = 'dubai-board';
const CAN_ZOOM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '2');

/* ─────────────────────────────────────────────────────────────── the circuit ── */

interface DealShow { deal: DubaiDeal | null; fresh: boolean; test: boolean; id: string }

/** [DUBAI-WIPES] What each page change carries: 1 the plane swoosh, 2 gate doors (DXB), 3 a departures-board flap panel,
 *  4 the Burj at dusk, 5 a cloud bank with a plane flying left. */
const BURJ = '<svg class="bj" viewBox="-70 0 140 560" preserveAspectRatio="xMidYMax meet" aria-hidden="true"><path d="M0 0 L5 80 L10 160 L16 250 L23 340 L31 430 L44 560 L-44 560 L-31 430 L-23 340 L-16 250 L-10 160 L-5 80 Z"></path><circle cx="0" cy="2" r="4"></circle></svg>';
const WIPES = [
  PLANE_SVG,
  '<i class="dl"></i><i class="dr"></i><span class="dt">DXB</span>',
  '<span class="bt">NOW BOARDING</span>',
  BURJ,
  PLANE_SVG,
];

/**
 * Runs the board: which page is up, for how long, the page-change wipe, the progress line and the New Deal takeover.
 * Plain DOM on purpose — the pages are drawn by flightPages.ts into one element, exactly as in the approved preview.
 */
class Circuit {
  model: Model | null = null;
  page: PageId = 'standings';
  private loop: Loop | null = null;
  private timer = 0;
  private switching = false;
  private takeover: { back: PageId; show: DealShow } | null = null;
  private queue: DealShow[] = [];
  private lastGuest: PageId | null = null;
  private dead = false;
  /** [DUBAI-SOUND-PAGE] Every New Deal showing (a new deal, a test, a hand-picked visit) deals the next sound from the deck. */
  private showings = 0;
  /** [DUBAI-RESUME] When the page on screen is due to change, and how much of its time a new deal's interruption left. */
  private due = 0;
  private resumeLeft = 0;

  constructor(
    private el: HTMLElement,
    private wipe: HTMLElement,
    private prog: HTMLElement,
    private onChange: (page: PageId, announcing: boolean) => void,
    private onSound: (id: string) => void,
  ) {}

  /** New figures. The first set starts the board; later ones redraw the page on screen (never a celebration in progress). */
  setModel(m: Model): void {
    const first = !this.model;
    this.model = m;
    if (this.dead) return;
    if (first) { this.go('standings', { instant: true }); return; }
    if (!this.switching && !this.takeover) this.draw(this.page, this.page === 'deal' ? this.guestDeal() : undefined);
  }

  /** A new deal (or a test): shown at once, or after the one already celebrating. */
  announce(show: DealShow): void {
    if (this.queue.some((q) => q.id === show.id) || this.takeover?.show.id === show.id) return;
    this.queue.push(show);
    if (!this.switching && !this.takeover) this.nextAnnouncement();
  }

  /** [DUBAI-FILL] The canvas got wider or narrower: the page on screen is drawn again for it (a celebration keeps its deal). */
  setWidth(w: number): void {
    if (this.el.dataset.cw === String(w)) return;
    this.el.dataset.cw = String(w);
    if (!this.model || this.switching || this.dead) return;
    this.draw(this.page, this.takeover ? this.takeover.show : (this.page === 'deal' ? this.guestDeal() : undefined));
  }

  /** A person picked a page: it holds for HOLD_MS, then the circuit carries on (a waiting announcement goes first). */
  pick(p: PageId): void {
    if (this.switching || !this.model) return;
    if (this.takeover) { this.takeover = null; this.resumeLeft = 0; stopCue(); }
    this.go(p, { dwell: HOLD_MS, show: p === 'deal' ? this.guestDeal() : undefined });
  }

  stop(): void {
    this.dead = true;
    window.clearTimeout(this.timer);
    this.loop?.stop();
    this.loop = null;
    stopCue();
  }

  private guestDeal(): DealShow {
    const d = this.model?.latest || null;
    return { deal: d, fresh: false, test: false, id: d ? d.id : 'none' };
  }

  private nextAnnouncement(): void {
    const show = this.queue.shift();
    if (!show) return;
    const back = this.takeover ? this.takeover.back : (this.page === 'deal' ? 'standings' : this.page);
    /* [DUBAI-RESUME 2026-10-19, user] "the auto thing which was interrupted will continue from where the new deal notifier
       comes": the interrupted page comes back for the time it still had (a New Deal visit that got interrupted hands back to
       Standings, which starts fresh). Taken at the FIRST interruption, so deals queued behind it keep the same hand-back. */
    if (!this.takeover) this.resumeLeft = back === this.page ? Math.max(0, this.due - Date.now()) : 0;
    this.takeover = { back, show };
    this.go('deal', { dwell: ANNOUNCE_MS, show });
  }

  /** [DUBAI-SOUND-PAGE] Is the New Deal page on the screen right now? Every New Deal page has its sound, so the "tap for
   *  sound" chip plays it there; on any other page the chip only wakes the audio. */
  dealOnScreen(): boolean {
    return this.page === 'deal' && !this.switching && !this.dead;
  }

  private draw(p: PageId, show?: DealShow): void {
    if (!this.model) return;
    this.loop?.stop();
    this.el.innerHTML = '';
    if (p === 'deal') {
      const s = show || this.guestDeal();
      this.loop = renderDealPage(this.el, this.model, { deal: s.deal, fresh: s.fresh, test: s.test, gifUrl: s.deal ? gifFor(s.id) : null });
      warmNextGif();
    } else {
      this.loop = FLIGHT_PAGES[p](this.el, this.model);
    }
  }

  private async go(p: PageId, o: { instant?: boolean; dwell?: number; show?: DealShow; resume?: boolean } = {}): Promise<void> {
    if (this.switching || this.dead) return;
    window.clearTimeout(this.timer);
    this.switching = true;
    const show = o.show || (p === 'deal' ? this.guestDeal() : undefined);
    // [DUBAI-CHASE-20] a Chase visit starts from its first group — unless it is the visit a new deal interrupted, coming back
    if (p === 'chase' && !o.resume) chaseVisit();
    /* [DUBAI-SOUND-PAGE 2026-10-19, user] "whenever we get to the new deal page then the sound should play, either when new
       sales comes or when i manually go there": EVERY arrival on the New Deal page deals the next sound of the deck — a new
       deal's celebration, a test (T), a person picking New Deal (bar button / key 5); the circuit itself never goes there
       ([DUBAI-DEAL-ONLY]). (Was [DUBAI-SOUND-ONLY-NEW]: new deals only, a hand-picked New Deal silent.) The sound starts the
       moment the page is drawn and fades out the moment the New Deal page starts to leave, whatever takes it away; a board
       out of sight stays silent. */
    if (this.page === 'deal') stopCue();
    const swap = (): void => {
      this.page = p;
      this.draw(p, show);
      if (p === 'deal' && show && show.deal) {
        this.showings += 1;
        this.onSound(`${show.id}#${Date.now()}-${this.showings}`);
      }
      this.onChange(p, !!this.takeover);
    };
    if (o.instant || REDUCED) swap();
    else await this.transition(swap);
    this.switching = false;
    if (this.dead) return;
    // an announcement that arrived during the wipe goes now
    if (!this.takeover && this.queue.length) { this.nextAnnouncement(); return; }
    this.schedule(o.dwell || DWELL[p]);
  }

  /** [DUBAI-WIPES 2026-10-19, user] "add 5 different variations and it will be used randomly for all the tab switch animations":
   *  one of five page changes per switch, never the same twice running (CSS: boardStyles .w1, extraStyles .v2-.v5). */
  private lastWipe = 0;
  private transition(swap: () => void): Promise<void> {
    return new Promise((resolve) => {
      let k = 1 + Math.floor(Math.random() * WIPES.length);
      if (k === this.lastWipe) k = (k % WIPES.length) + 1;
      this.lastWipe = k;
      const cls = k === 1 ? 'w1' : `v${k}`;
      const band = this.wipe.querySelector('.band') as HTMLElement;
      band.innerHTML = WIPES[k - 1];
      this.wipe.className = `wipe ${cls} in`;
      this.wipe.hidden = false;
      window.setTimeout(() => {
        if (!this.dead) swap();
        this.wipe.className = `wipe ${cls} out`;
        window.setTimeout(() => { this.wipe.hidden = true; this.wipe.className = 'wipe'; resolve(); }, 580);
      }, 520);
    });
  }

  private schedule(ms: number): void {
    window.clearTimeout(this.timer);
    const prog = this.prog;
    prog.style.transition = 'none';
    prog.style.width = '0%';
    void prog.offsetWidth;
    prog.classList.remove('off');
    prog.style.transition = `width ${ms}ms linear`;
    prog.style.width = '100%';
    this.due = Date.now() + ms;
    this.timer = window.setTimeout(() => this.next(), ms);
  }

  private next(): void {
    if (this.dead) return;
    if (this.queue.length) { this.nextAnnouncement(); return; }
    if (this.takeover) {
      const back = this.takeover.back;
      const left = this.resumeLeft;
      this.takeover = null;
      this.resumeLeft = 0;
      stopCue();
      void this.go(back, { dwell: left > 0 ? Math.max(10000, left) : DWELL[back], resume: left > 0 }); // [DUBAI-RESUME] the time it still had
      return;
    }
    if (this.page !== 'standings') { void this.go('standings'); return; }
    const pool = GUESTS.filter((g) => g !== this.lastGuest);
    const pick = pool[Math.floor(Math.random() * pool.length)] || GUESTS[0];
    this.lastGuest = pick;
    void this.go(pick);
  }
}

/* ─────────────────────────────────────────────────────────────── the page ── */

const LockScroll = createGlobalStyle`
  [data-page-scroll] { overflow: hidden !important; }
`;

/** The screen and its letterbox; above the page card, under the floating profile (sign-out stays reachable).
 *  [DUBAI-TV 2026-10-19, user] "this dashboard should adapt all the screen": a screen of another shape gets bands in the
 *  board's own sky colours (top half the sky's top colour, bottom half its bottom colour), never black bars. */
const Frame = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  background: linear-gradient(180deg, #0c2136 0%, #0c2136 50%, #06111d 50%, #06111d 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stage = styled.div<{ $scale: number; $width: number }>`
  position: relative;
  width: ${(p) => p.$width}px;
  height: ${H}px;
  flex: 0 0 auto;
  overflow: hidden;
  ${(p) => (CAN_ZOOM ? `zoom: ${p.$scale};` : `transform: scale(${p.$scale}); transform-origin: center center;`)}
`;

const Cover = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: radial-gradient(1100px 620px at 28% 30%, #123553 0%, rgba(18, 53, 83, 0) 62%), linear-gradient(180deg, #0c2136 0%, #06111d 100%);
  color: #eaf1f7;
  font: 700 30px/1.2 'B612', sans-serif;
  letter-spacing: .02em;
  .plane { width: 90px; height: 90px; }
  small { font: 700 15px/1 'B612 Mono', monospace; letter-spacing: .22em; color: #ffb547; text-transform: uppercase; }
`;

/* [DUBAI-UPDATE 2026-10-19, user] "make it the same just like we have in the pm singapore dashboard that if i deploy then the
   dashboard will show a screen for that and then the changes will take place on the dashboard, so that i dont refresh the
   page": PM Singapore's chequered flag, in this board's own language. When the board takes a new build, the gate doors close
   over the WHOLE screen (above everything, the sign-out circle too), DXB on the amber seam, a departures plaque says the
   board is updating and a plane crosses the sky. It is held FLAG_MS and the reload runs underneath it; it is never taken
   down, so the room sees the gate and then the new board (its own "Boarding the latest figures" cover). Transforms and
   opacity only (on the compositor); the text grows with the board's scale (--s). */
const gateL = keyframes`
  from { transform: translateX(-102%); }
  to { transform: none; }
`;
const gateR = keyframes`
  from { transform: translateX(102%); }
  to { transform: none; }
`;
const gateText = keyframes`
  0%, 45% { opacity: 0; }
  100% { opacity: 1; }
`;
const gateJet = keyframes`
  from { transform: translateX(-12vw); }
  to { transform: translateX(112vw); }
`;
const UpdateGate = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  overflow: hidden;
  background: #06111d;
  .gl, .gr {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50.2%;
    background: repeating-linear-gradient(90deg, rgba(255, 255, 255, .03) 0 2px, rgba(255, 255, 255, 0) 2px 120px), linear-gradient(180deg, #0e2740, #07162a);
    animation: 420ms cubic-bezier(.6, 0, .4, 1) both;
  }
  .gl { left: 0; box-shadow: inset calc(-5px * var(--s, 1)) 0 0 #ffb547; animation-name: ${gateL}; }
  .gr { right: 0; box-shadow: inset calc(5px * var(--s, 1)) 0 0 #ffb547; animation-name: ${gateR}; }
  .in {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: calc(34px * var(--s, 1));
    animation: ${gateText} 700ms ease-out both;
  }
  .mark { padding-left: .3em; font: 700 calc(120px * var(--s, 1))/1 'B612', sans-serif; letter-spacing: .3em; color: #ffb547; }
  .plq {
    padding: calc(22px * var(--s, 1)) calc(40px * var(--s, 1));
    border-radius: calc(12px * var(--s, 1));
    background: #07131f; /* solid: the gate's seam must not show through the words */
    box-shadow: inset 0 0 0 1px rgba(255, 181, 71, .45);
    text-align: center;
  }
  .plq small { display: block; padding-left: .24em; font: 700 calc(15px * var(--s, 1))/1 'B612 Mono', monospace; letter-spacing: .24em; text-transform: uppercase; color: #a9bbcb; }
  .plq b { display: block; margin-top: calc(16px * var(--s, 1)); padding-left: .3em; font: 700 calc(40px * var(--s, 1))/1 'B612 Mono', monospace; letter-spacing: .3em; color: #ffb547; white-space: nowrap; }
  .plq span { display: block; margin-top: calc(16px * var(--s, 1)); font: 700 calc(24px * var(--s, 1))/1.2 'B612', sans-serif; color: #eaf1f7; }
  .jet { position: absolute; left: 0; top: 14%; width: calc(84px * var(--s, 1)); height: calc(84px * var(--s, 1)); animation: ${gateJet} 5600ms linear both; }
  .jet .plane { display: block; width: 100%; height: 100%; }
  @media (prefers-reduced-motion: reduce) {
    .gl, .gr, .in, .jet { animation: none; }
  }
`;

const TapChip = styled.button`
  position: absolute;
  right: 48px;
  bottom: 54px;
  z-index: 46;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid #ffb547;
  background: rgba(6, 17, 29, .88);
  color: #ffb547;
  font: 700 14px/1 'B612 Mono', monospace;
  letter-spacing: .12em;
  cursor: pointer;
`;

/**
 * [DUBAI-BAR 2026-10-19, user] "the bottom pill will only come when i take my cursor to the very end in the middle side":
 * the bar lives in a thin strip along the very bottom edge, in the middle, and shows only while the pointer is in that
 * strip or on the bar itself (the bar is inside the strip, so moving up onto it keeps it) — a mouse anywhere else on the
 * board never brings it up. Keyboard focus shows it too. Keys 1-5 / t / m work without it.
 */
const BarZone = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  z-index: 61;
  width: 760px;
  height: 18px;
  transform: translateX(-50%);
  > nav { opacity: 0; pointer-events: none; transform: translateX(-50%) translateY(24px); }
  &:hover > nav, &:focus-within > nav { opacity: 1; pointer-events: auto; transform: translateX(-50%) translateY(0); }
`;

const Bar = styled.nav`
  position: absolute;
  left: 50%;
  bottom: 14px;
  display: flex;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(6, 17, 29, .9);
  border: 1px solid rgba(255, 255, 255, .12);
  white-space: nowrap;
  transition: opacity .25s ease, transform .25s ease;
  button {
    border: 0;
    border-radius: 999px;
    padding: 8px 14px;
    background: transparent;
    color: #eaf1f7;
    font: 700 13px/1 'B612', sans-serif;
    cursor: pointer;
  }
  button[aria-pressed='true'] { background: #ffb547; color: #142230; }
  button:focus-visible { outline: 2px solid #ffb547; outline-offset: 2px; }
`;

async function getBoard(stamp: string): Promise<{ status: number; body: any }> {
  const r = await fetch(`/api/dubai-contest/board${stamp ? `?stamp=${encodeURIComponent(stamp)}` : ''}`, {
    credentials: 'include', headers: { 'Cache-Control': 'no-store' },
  });
  if (!r.ok) return { status: r.status, body: null };
  return { status: r.status, body: await r.json() };
}

interface Fit { scale: number; width: number }
/** The canvas that fills this screen: 900 tall, as wide as the screen's shape (clamped), scaled to fit. */
function fit(): Fit {
  if (typeof window === 'undefined') return { scale: 1, width: W };
  const vw = window.innerWidth; const vh = window.innerHeight;
  if (vw <= 0 || vh <= 0) return { scale: 1, width: W };
  const width = Math.round(Math.min(MAX_W, Math.max(W, (vw * H) / vh)));
  return { scale: Math.min(vw / width, vh / H), width };
}

const DubaiContestPage: React.FC = () => {
  const boardRef = useRef<HTMLDivElement>(null);
  const wipeRef = useRef<HTMLDivElement>(null);
  const progRef = useRef<HTMLDivElement>(null);
  const circuit = useRef<Circuit | null>(null);
  const lastSoundId = useRef('');
  const [canvas, setCanvas] = useState<Fit>(fit);
  const canvasRef = useRef(canvas);
  canvasRef.current = canvas;
  const [ready, setReady] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [page, setPage] = useState<PageId>('standings');
  const [muted, setMutedState] = useState(isMuted);
  const [flagging, setFlagging] = useState(false);

  /* the canvas follows the screen (a TV can renegotiate its resolution under a running page) */
  useEffect(() => {
    const apply = (): void => setCanvas((prev) => {
      const next = fit();
      return Math.abs(prev.scale - next.scale) < 1e-4 && prev.width === next.width ? prev : next;
    });
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => { window.removeEventListener('resize', apply); window.removeEventListener('orientationchange', apply); };
  }, []);

  /* the board: the circuit, the 4-second poll, the announcements */
  useEffect(() => {
    if (!boardRef.current || !wipeRef.current || !progRef.current) return undefined;
    const sound = (id: string): void => {
      lastSoundId.current = id;
      if (document.hidden) return; // [DUBAI-SOUND-ONLY-NEW] a board nobody can see stays silent
      void playCue(id).then(() => setNeedTap(wasBlocked()));
    };
    boardRef.current.dataset.cw = String(canvasRef.current.width); // [DUBAI-FILL] before the first page is drawn
    const c = new Circuit(boardRef.current, wipeRef.current, progRef.current, (p) => setPage(p), sound);
    circuit.current = c;
    const disarm = armAudio(() => setNeedTap(false));
    primeAudio(); // the audio context exists before the first celebration
    const render = window.setTimeout(() => { void prepareSounds(); }, 2500); // the five cues rendered once, after the first paint
    // [DUBAI-SOUND-ONLY-NEW 2026-10-19, user] "the music used to play in the backend": the moment the board goes out of sight
    // (another tab, a minimised window) any sound fades out, and none starts while it is hidden.
    const onVisibility = (): void => { if (document.hidden) stopCue(); };
    document.addEventListener('visibilitychange', onVisibility);
    let stamp = '';
    let seenAt: number | null = null;
    let alive = true;

    const tick = async (): Promise<void> => {
      if (document.hidden) return;
      try {
        const { status, body } = await getBoard(stamp);
        if (!alive) return;
        /* [DUBAI-NO-LINE 2026-10-19, user] no status line on the board ("i dont want to see this line"): a dropped connection
           just keeps the last figures and carries on by itself; an ended session goes back to the sign-in page (at most once
           in five minutes). */
        if (status === 401) {
          let last = 0;
          try { last = Number(window.sessionStorage.getItem(AUTH_RELOAD_KEY) || 0); } catch { last = 0; }
          if (Date.now() - last > RELOAD_RETRY_MS) {
            try { window.sessionStorage.setItem(AUTH_RELOAD_KEY, String(Date.now())); } catch { /* this page only */ }
            window.location.reload();
          }
          return;
        }
        if (!body || body.unchanged) return;
        const payload = body as DubaiPayload;
        stamp = payload.stamp;
        const model = buildModel(payload);
        // which deals are NEW to this screen (the first payload only primes — see SEEN_KEY)
        const ats = payload.feed.map((f) => Date.parse(f.at)).filter((n) => Number.isFinite(n));
        const newest = ats.length ? Math.max(...ats) : 0;
        if (seenAt == null) {
          let stored = 0;
          try { stored = Number(window.localStorage.getItem(SEEN_KEY) || 0); } catch { stored = 0; }
          seenAt = stored && Date.now() - stored < SEEN_WINDOW_MS ? Math.min(stored, newest || stored) : newest;
        }
        const fresh = payload.feed
          .filter((f) => Date.parse(f.at) > (seenAt as number))
          .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
        c.setModel(model);
        setReady(true);
        if (fresh.length) {
          seenAt = Math.max(seenAt, ...fresh.map((f) => Date.parse(f.at)));
          try { window.localStorage.setItem(SEEN_KEY, String(seenAt)); } catch { /* this page only */ }
          fresh.forEach((f) => {
            const deal = model.deals.find((d) => d.id === f.id) || null;
            if (deal) c.announce({ deal, fresh: true, test: false, id: f.id });
          });
        }
      } catch { /* offline or mid-deploy: the board keeps its last figures and the next poll tries again */ }
    };

    void tick();
    const iv = window.setInterval(() => { void tick(); }, POLL_MS);
    const warm = window.setTimeout(warmNextGif, 10000);
    return () => {
      alive = false;
      window.clearInterval(iv);
      window.clearTimeout(warm);
      window.clearTimeout(render);
      document.removeEventListener('visibilitychange', onVisibility);
      disarm();
      c.stop();
      circuit.current = null;
    };
  }, []);

  /* [DUBAI-FILL] a new canvas width redraws the page on screen for it */
  useEffect(() => { circuit.current?.setWidth(canvas.width); }, [canvas.width]);

  /* keys: 1-5 pick a page (5 = New Deal, with its sound), t replays the latest deal as a test (GIF + sound), m mutes */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const c = circuit.current;
      if (!c || !c.model) return;
      const n = Number(e.key);
      if (n >= 1 && n <= 5) { c.pick(PAGES[n - 1].id); return; }
      if (e.key === 't' || e.key === 'T') {
        const d = c.model.latest;
        if (d) c.announce({ deal: d, fresh: true, test: true, id: `test-${Date.now()}` });
        return;
      }
      if (e.key === 'm' || e.key === 'M') { const v = !isMuted(); setMuted(v); setMutedState(v); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ── the unattended reload (PMSingaporePage's rules: settle 3 min, probe, never chase a build twice in 5 min) ──
     [DUBAI-UPDATE 2026-10-19] The board owns its update. The app-wide updater (UpdateBanner) reloads every tab after a deploy
     too — plainly, no screen — and on the TV it can get there first; so it is held off while the board is up (reloadGuard's
     holdReload, released when the board closes) and only this reload, behind the update screen, ever runs here. */
  useEffect(() => {
    const mine = process.env.REACT_APP_BUILD;
    if (!mine) return undefined; // dev build — no version stamp
    holdReload(BOARD_HOLD);
    let cancelled = false;
    let seen: { build: string; at: number } | null = null;
    const check = async (): Promise<void> => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok || cancelled) return;
        const { build } = await r.json();
        if (!build || build === mine) return;
        if (!seen || seen.build !== build) { seen = { build, at: Date.now() }; return; }
        if (Date.now() - seen.at < SETTLE_MS) return;
        const guard = (sessionStorage.getItem(RELOAD_KEY) || '').split('|');
        if (guard[0] === build && Date.now() - Number(guard[1] || 0) < RELOAD_RETRY_MS) return;
        const probe = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
        if (!probe.ok || cancelled) return;
        sessionStorage.setItem(RELOAD_KEY, `${build}|${Date.now()}`);
        /* [DUBAI-UPDATE] Screen first, reload underneath it (PM Singapore's order). The screen is deliberately NOT taken
           down before the reload: it stays painted until the new page replaces the whole document, which is what covers the
           gap while the new bundle loads. Reduced motion: straight to the reload, as PM Singapore does. */
        if (REDUCED) { window.location.reload(); return; }
        setFlagging(true);
        window.setTimeout(() => window.location.reload(), FLAG_MS);
      } catch { /* offline or mid-deploy — next cycle */ }
    };
    const iv = window.setInterval(check, VERSION_POLL_MS);
    return () => { cancelled = true; window.clearInterval(iv); releaseReload(BOARD_HOLD); };
  }, []);

  return createPortal(
    <>
      <LockScroll />
      <DubaiBoardStyles />
      <DubaiExtraStyles />
      <Frame>
        <Stage $scale={canvas.scale} $width={canvas.width} className="dxb">
          <div className="board l1" ref={boardRef} />
          <div className="progress off" ref={progRef} style={{ ['--prog' as string]: '#ffb547' } as React.CSSProperties} />
          <div className="wipe" hidden ref={wipeRef}><div className="band" /></div>
          {!ready && (
            <Cover>
              <span dangerouslySetInnerHTML={{ __html: PLANE_SVG }} />
              <small>GITEX GLOBAL Dubai Contest</small>
              <span>Boarding the latest figures…</span>
            </Cover>
          )}
          {needTap && !muted && (
            <TapChip
              type="button"
              onClick={() => {
                // [DUBAI-SOUND-PAGE] on the New Deal page the tap plays its sound; on any other page it only unlocks the sound
                if (circuit.current?.dealOnScreen()) void playCue(lastSoundId.current || 'tap').then(() => setNeedTap(wasBlocked()));
                else void unlockAudio().then((ok) => setNeedTap(!ok));
              }}
            >
              🔇 TAP FOR SOUND
            </TapChip>
          )}
        </Stage>
        {/* [DUBAI-BAR] no hover text on the board (user: "remove the tooltip from the dubai sales dashboard").
            [DUBAI-TV] the strip sits on the SCREEN's bottom edge (on a screen of another shape the board ends above it), at the
            board's scale. */}
        <BarZone style={CAN_ZOOM ? { zoom: canvas.scale } : undefined}>
          <Bar aria-label="Board pages">
            {PAGES.map((p) => (
              <button key={p.id} type="button" aria-pressed={page === p.id} onClick={() => circuit.current?.pick(p.id)}>
                {p.label}
              </button>
            ))}
            <button type="button" aria-pressed={!muted} onClick={() => { const v = !muted; setMuted(v); setMutedState(v); }}>
              {muted ? '🔇 Sound off' : '🔊 Sound on'}
            </button>
          </Bar>
        </BarZone>
      </Frame>
      {/* [DUBAI-UPDATE] The update screen sits OUTSIDE the board on purpose (PM Singapore's flag rule): nothing may show through
          it — the sky bands and the floating sign-out circle (z 1200) live outside the canvas too. Its text gets the board's
          scale by hand (--s), so it grows with the screen like everything else. */}
      {flagging && (
        <UpdateGate data-update-gate="" aria-hidden="true" style={{ ['--s' as string]: canvas.scale } as React.CSSProperties}>
          <i className="gl" />
          <i className="gr" />
          <div className="in">
            <div className="mark">DXB</div>
            <div className="plq">
              <small>GITEX GLOBAL Dubai Contest</small>
              <b>NEW VERSION · NOW BOARDING</b>
              <span>The board is updating — back in a moment</span>
            </div>
          </div>
          <span className="jet" dangerouslySetInnerHTML={{ __html: PLANE_SVG }} />
        </UpdateGate>
      )}
    </>,
    document.body,
  );
};

export default DubaiContestPage;
